/**
 * Google Ads — the full Google Ads page.
 *
 *   1. Performance  the Ad Dashboard's Google band, framed from /api/ads/embed?section=google.
 *                   Same code and same readings as /ads, so the two cannot disagree. The frame
 *                   reports its height by postMessage and the iframe is sized to it.
 *   2. Landing page the /first-chapter funnel, with Google's own spend and clicks (the nightly
 *                   Google Ads API readings, pro-rated by day) wired in as cost.
 *   3. Campaigns    one row per campaign from the same readings: newest week and a 4-week trend.
 *
 * The landing section is styled to sit beside Website Traffic: same KPI card shape, same Montserrat
 * micro-labels, same range control. Every colour is a brandColors constant.
 *
 * ⚠️ THREE THINGS ON THIS SCREEN ARE DELIBERATELY NOT NUMBERS.
 *
 * 1. Attributed signups. Nothing joins a store click to an install: no Play URL
 *    carries a referrer, gclid is stored nowhere, `users` has no acquisition
 *    columns, and nothing writes an install row into singular_events. The card
 *    states that and names the unlock. It must never render as 0 — an operator
 *    who reads "0 signups" concludes the campaign failed, when the truth is
 *    that nobody is counting.
 * 2. Cost, when no Google Ads reading has ever been stored. "$0 spent" on a
 *    live campaign is a lie that survives review; "not connected" is not.
 * 3. Any ratio whose denominator is zero. The server sends null and we render
 *    an em dash, never 0% and never Infinity.
 *
 * The landings figure is page views, not visitors: an in-page anchor CTA
 * generates a second page view and nothing stored can distinguish it from a
 * genuine landing. That is stated under the funnel rather than hidden in a
 * tooltip. (An earlier version claimed to filter these out with `hash IS NULL`
 * — there is no such column, and that query 500'd the endpoint.)
 */

import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import { Box, Typography, Paper, Skeleton, Button, Tooltip, Chip } from '@mui/material';
import {
  ComposedChart, Bar, Line, XAxis, YAxis, CartesianGrid, Tooltip as ReTooltip,
  ResponsiveContainer, Legend,
} from 'recharts';
import DownloadIcon from '@mui/icons-material/Download';
import TimeRangeFilter from '../common/TimeRangeFilter';
import useTimeRangeParam from '../common/useTimeRangeParam';
import EmptyState from '../common/EmptyState';
import { TimeRangeValue } from '../common/timeRange';
import { brandColors, brandFonts } from '../../theme/brandColors';
import googleAdsLandingApi, {
  num, SummaryResponse, TimeseriesResponse, CtaBreakdownResponse, UtmBreakdownResponse, RangeMeta,
  CampaignsResponse, CampaignRow, CampaignTrendWeek,
} from './api';

const LANDING_URL = 'https://trylifescribe.com/first-chapter';

// ─── formatting ───────────────────────────────────────────────────────────────

/** An absent measurement is an em dash. Never 0, never NaN, never Infinity. */
const dash = '—';

const fmtInt = (v: number | string | null | undefined): string =>
  v === null || v === undefined ? dash : num(v).toLocaleString();

const fmtPct = (r: number | null | undefined): string =>
  r === null || r === undefined ? dash : `${(r * 100).toFixed(1)}%`;

const fmtUsd = (v: number | string | null | undefined): string =>
  v === null || v === undefined ? dash : `$${num(v).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

const fmtTime = (sec: number | null): string => {
  if (sec === null || sec === undefined) return dash;
  const m = Math.floor(sec / 60);
  return `${m}m ${String(sec % 60).padStart(2, '0')}s`;
};

// ─── KPI card (same shape as the Website Traffic screen) ─────────────────────

interface KpiProps {
  label: string;
  value: React.ReactNode;
  sub?: React.ReactNode;
  topColor: string;
  loading: boolean;
  /** Renders the value muted, for a figure that is unavailable rather than low. */
  muted?: boolean;
  help?: string;
}

const Kpi: React.FC<KpiProps> = ({ label, value, sub, topColor, loading, muted, help }) => (
  <Paper sx={{ p: 2.5, borderRadius: '14px', border: '1px solid #E5E7EB', borderTop: `3px solid ${topColor}`, boxShadow: 'none', height: '100%' }}>
    <Tooltip title={help || ''} placement="top" disableHoverListener={!help}>
      <Typography sx={{ fontFamily: brandFonts.accent, fontSize: '10px', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.7px', color: brandColors.slateGray, mb: 0.5, cursor: help ? 'help' : 'default' }}>
        {label}
      </Typography>
    </Tooltip>
    {loading ? (
      <Skeleton variant="text" width={90} height={40} />
    ) : (
      <Typography sx={{ fontFamily: brandFonts.accent, fontSize: '28px', fontWeight: 800, color: muted ? brandColors.slateGray : '#111827', lineHeight: 1.1, mb: 0.3 }}>
        {value}
      </Typography>
    )}
    {!loading && sub && (
      <Typography sx={{ fontFamily: brandFonts.accent, fontSize: '11px', color: brandColors.slateGray, fontWeight: 500 }}>
        {sub}
      </Typography>
    )}
  </Paper>
);

// ─── Range transparency line ──────────────────────────────────────────────────

const RangeMetaLine: React.FC<{ meta?: RangeMeta }> = ({ meta }) => {
  if (!meta) return null;
  return (
    <Typography sx={{ fontFamily: brandFonts.accent, fontSize: '11px', color: brandColors.slateGray, mb: 1.5 }}>
      Showing {meta.effective_from} → {meta.effective_to}
      {meta.epoch_clamped && (
        <> · <strong>window clamped</strong> to the page's launch on {meta.landing_epoch}; there is no earlier data because the page did not exist</>
      )}
    </Typography>
  );
};

// ─── Funnel strip ─────────────────────────────────────────────────────────────

interface FunnelStep {
  label: string;
  value: React.ReactNode;
  color: string;
  note?: string;
  unavailable?: boolean;
}

const Funnel: React.FC<{ steps: FunnelStep[]; loading: boolean }> = ({ steps, loading }) => (
  <Box sx={{ display: 'flex', alignItems: 'stretch', gap: 1, flexWrap: 'wrap', mb: 1 }}>
    {steps.map((s, i) => (
      <React.Fragment key={s.label}>
        <Paper sx={{
          flex: '1 1 150px', p: 2, borderRadius: '12px', boxShadow: 'none',
          border: `1px solid ${s.unavailable ? '#E5E7EB' : 'transparent'}`,
          bgcolor: s.unavailable ? brandColors.softCream : s.color,
        }}>
          <Typography sx={{ fontFamily: brandFonts.accent, fontSize: '9px', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.8px', color: s.unavailable ? brandColors.slateGray : 'rgba(255,255,255,0.85)' }}>
            {s.label}
          </Typography>
          {loading ? <Skeleton variant="text" width={60} height={34} /> : (
            <Typography sx={{ fontFamily: brandFonts.accent, fontSize: s.unavailable ? '13px' : '24px', fontWeight: 800, color: s.unavailable ? brandColors.slateGray : brandColors.white, lineHeight: 1.25, mt: 0.3 }}>
              {s.value}
            </Typography>
          )}
          {!loading && s.note && (
            <Typography sx={{ fontFamily: brandFonts.accent, fontSize: '10px', color: s.unavailable ? brandColors.slateGray : 'rgba(255,255,255,0.8)', mt: 0.3 }}>
              {s.note}
            </Typography>
          )}
        </Paper>
        {i < steps.length - 1 && (
          <Box sx={{ display: 'flex', alignItems: 'center', color: brandColors.slateGray, fontSize: '18px' }}>→</Box>
        )}
      </React.Fragment>
    ))}
  </Box>
);

// ─── Section heading ──────────────────────────────────────────────────────────

const SectionHeading: React.FC<{ title: string; sub?: React.ReactNode; action?: React.ReactNode }> = ({ title, sub, action }) => (
  <Box sx={{ display: 'flex', alignItems: 'flex-end', gap: 2, mb: 1.5, flexWrap: 'wrap' }}>
    <Box>
      <Typography component="h2" sx={{ fontFamily: brandFonts.heading, fontSize: '19px', fontWeight: 600, color: '#111827' }}>
        {title}
      </Typography>
      {sub && (
        <Typography sx={{ fontFamily: brandFonts.accent, fontSize: '12px', color: brandColors.slateGray }}>
          {sub}
        </Typography>
      )}
    </Box>
    <Box sx={{ flexGrow: 1 }} />
    {action}
  </Box>
);

// ─── Performance: the Ad Dashboard's Google band, framed ─────────────────────

/** The same document /ads renders, cut down to the Google band. */
export const PERFORMANCE_SRC = '/api/ads/embed?section=google';
const FRAME_MIN_HEIGHT = 320;
const FRAME_MAX_HEIGHT = 6000;

const PerformanceFrame: React.FC = () => {
  const frameRef = useRef<HTMLIFrameElement | null>(null);
  // Null until the frame's first height message; the skeleton covers it until then.
  const [height, setHeight] = useState<number | null>(null);

  useEffect(() => {
    const onMessage = (event: MessageEvent) => {
      if (event.origin !== window.location.origin) return;
      if (!frameRef.current || event.source !== frameRef.current.contentWindow) return;
      const data = event.data as { type?: string; height?: unknown } | null;
      if (!data || data.type !== 'ls-ads:height') return;
      if (typeof data.height !== 'number' || !Number.isFinite(data.height)) return;
      setHeight(Math.min(FRAME_MAX_HEIGHT, Math.max(FRAME_MIN_HEIGHT, Math.ceil(data.height))));
    };
    window.addEventListener('message', onMessage);
    return () => window.removeEventListener('message', onMessage);
  }, []);

  return (
    <Paper sx={{ borderRadius: '14px', border: '1px solid #E5E7EB', boxShadow: 'none', overflow: 'hidden' }}>
      <Box sx={{ position: 'relative', height: height ?? FRAME_MIN_HEIGHT }}>
        {height === null && (
          <Skeleton variant="rectangular" data-testid="performance-skeleton" sx={{ position: 'absolute', inset: 0, zIndex: 1 }} />
        )}
        <iframe
          ref={frameRef}
          title="Google Ads performance"
          src={PERFORMANCE_SRC}
          sandbox="allow-scripts allow-same-origin allow-popups allow-popups-to-escape-sandbox"
          style={{ border: 0, width: '100%', height: '100%', display: 'block', background: '#fff' }}
        />
      </Box>
    </Paper>
  );
};

// ─── Campaigns ────────────────────────────────────────────────────────────────

/** 'YYYY-MM-DD' → 'Oct 23', read as UTC so the label never shifts a day. */
const fmtDay = (ymd: string): string => {
  const d = new Date(`${ymd}T00:00:00Z`);
  return Number.isNaN(d.getTime()) ? ymd : d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', timeZone: 'UTC' });
};

const TREND_HEIGHT = 26;

/** Four weekly spend bars, scaled within the row. A week still in progress is drawn paler. */
const SpendTrend: React.FC<{ weeks: CampaignTrendWeek[] }> = ({ weeks }) => {
  const max = Math.max(0, ...weeks.map((w) => w.spend));
  return (
    <Box role="img" aria-label={`Weekly spend: ${weeks.map((w) => `${fmtDay(w.friday)} ${fmtUsd(w.spend)}`).join(', ')}`}
      sx={{ display: 'inline-flex', alignItems: 'flex-end', gap: '3px', height: TREND_HEIGHT }}>
      {weeks.map((w) => (
        <Tooltip key={w.friday} placement="top"
          title={`Week to ${fmtDay(w.friday)}: ${fmtUsd(w.spend)}${w.partial ? ' (week in progress)' : ''}`}>
          <Box sx={{
            width: 9, borderRadius: '2px 2px 0 0', bgcolor: brandColors.deepTeal, opacity: w.partial ? 0.4 : 1,
            height: max > 0 ? Math.max(2, Math.round((w.spend / max) * TREND_HEIGHT)) : 2,
          }} />
        </Tooltip>
      ))}
    </Box>
  );
};

/** Change in spend against the week before. Never against a week still in progress. */
const trendNote = (c: CampaignRow): string => {
  if (!c.active_latest_week) return 'no activity';
  if (c.partial) return 'week in progress';
  const prev = c.trend.length >= 2 ? c.trend[c.trend.length - 2].spend : 0;
  const cur = c.spend ?? 0;
  if (prev <= 0) return 'new';
  const pct = Math.round(((cur - prev) / prev) * 100);
  return `${pct > 0 ? '+' : ''}${pct}% vs prior week`;
};

const CampaignsTable: React.FC<{ data: CampaignsResponse }> = ({ data }) => {
  const rows = data.campaigns;
  const active = rows.filter((r) => r.active_latest_week);
  const total = active.reduce((a, r) => ({ spend: a.spend + num(r.spend), clicks: a.clicks + num(r.clicks), conv: a.conv + num(r.conv) }), { spend: 0, clicks: 0, conv: 0 });
  const th = { textAlign: 'left', pb: 1, color: brandColors.slateGray, fontSize: '10px', textTransform: 'uppercase', letterSpacing: '0.6px', borderBottom: '1px solid #E5E7EB', whiteSpace: 'nowrap', fontWeight: 700 } as const;
  const right = { textAlign: 'right' } as const;
  return (
    <Box sx={{ overflowX: 'auto' }}>
      <Box component="table" sx={{ width: '100%', minWidth: 760, borderCollapse: 'collapse', fontFamily: brandFonts.accent, fontSize: '12px' }}>
        <Box component="thead">
          <Box component="tr" sx={{ '& th': th }}>
            <th scope="col">Campaign</th>
            <th scope="col">Type</th>
            <th scope="col" style={right}>Spend</th>
            <th scope="col" style={right}>Clicks</th>
            <th scope="col" style={right}>Conversions</th>
            <th scope="col" style={right}>Cost / conversion</th>
            <th scope="col">4-week spend</th>
          </Box>
        </Box>
        <Box component="tbody">
          {rows.map((c) => (
            <Box component="tr" key={c.name} sx={{ '& td': { py: 1, borderBottom: '1px solid #F3F4F6', verticalAlign: 'middle' } }}>
              <td style={{ fontWeight: 600, color: '#111827' }}>
                {c.name}
                {c.partial && (
                  <Chip label="partial week" size="small"
                    sx={{ ml: 1, height: 18, fontFamily: brandFonts.accent, fontSize: '9px', fontWeight: 800, bgcolor: brandColors.softCream, color: brandColors.deepTeal }} />
                )}
              </td>
              <td style={{ color: brandColors.slateGray }}>{c.type_label}</td>
              <td style={{ ...right, fontWeight: 700 }}>{fmtUsd(c.spend)}</td>
              <td style={right}>{fmtInt(c.clicks)}</td>
              <td style={right}>{fmtInt(c.conv)}</td>
              <td style={right}>{fmtUsd(c.cost_per_conv)}</td>
              <td>
                <Box sx={{ display: 'flex', alignItems: 'flex-end', gap: 1 }}>
                  <SpendTrend weeks={c.trend} />
                  <Typography sx={{ fontFamily: brandFonts.accent, fontSize: '10.5px', color: brandColors.slateGray }}>{trendNote(c)}</Typography>
                </Box>
              </td>
            </Box>
          ))}
          {active.length > 1 && (
            <Box component="tr" sx={{ '& td': { py: 1, fontWeight: 700, color: '#111827' } }}>
              <td>All campaigns</td>
              <td />
              <td style={right}>{fmtUsd(total.spend)}</td>
              <td style={right}>{fmtInt(total.clicks)}</td>
              <td style={right}>{fmtInt(total.conv)}</td>
              <td style={right}>{total.conv > 0 ? fmtUsd(total.spend / total.conv) : dash}</td>
              <td />
            </Box>
          )}
        </Box>
      </Box>
    </Box>
  );
};

// ─── Page ─────────────────────────────────────────────────────────────────────

const GoogleAdsLandingPage: React.FC = () => {
  // Independent windows, each namespacing its own URL keys so they cannot
  // clobber one another.
  const [summaryRange, setSummaryRange] = useTimeRangeParam({ type: '30d' }, { prefix: 'summary' });
  const [chartRange, setChartRange]     = useTimeRangeParam({ type: '30d' }, { prefix: 'chart' });
  const [tableRange, setTableRange]     = useTimeRangeParam({ type: '30d' }, { prefix: 'table' });

  const [summary, setSummary] = useState<SummaryResponse | null>(null);
  const [series, setSeries]   = useState<TimeseriesResponse | null>(null);
  const [cta, setCta]         = useState<CtaBreakdownResponse | null>(null);
  const [utm, setUtm]         = useState<UtmBreakdownResponse | null>(null);
  const [campaigns, setCampaigns] = useState<CampaignsResponse | null>(null);

  const [loadingSummary, setLoadingSummary] = useState(true);
  const [loadingChart, setLoadingChart]     = useState(true);
  const [loadingTables, setLoadingTables]   = useState(true);
  const [loadingCampaigns, setLoadingCampaigns] = useState(true);

  // One error per resource, so a single failing card cannot blank the page.
  const [summaryError, setSummaryError] = useState<string | null>(null);
  const [chartError, setChartError]     = useState<string | null>(null);
  const [tableError, setTableError]     = useState<string | null>(null);
  const [campaignsError, setCampaignsError] = useState<string | null>(null);

  // Each loader takes a liveness probe and checks it AFTER every await, before
  // touching state. The previous form — `load(r).then(() => { if (x) return; })`
  // — was a no-op: the loader had already called setState by the time the flag
  // was read, so switching range twice quickly let the slower, older response
  // land last and overwrite the newer one.
  type Alive = () => boolean;

  const loadSummary = useCallback(async (r: TimeRangeValue, alive: Alive) => {
    setLoadingSummary(true); setSummaryError(null);
    try {
      const data = await googleAdsLandingApi.getSummary(r);
      if (!alive()) return;
      setSummary(data);
    } catch (e) {
      if (!alive()) return;
      setSummaryError(e instanceof Error ? e.message : 'Unknown error');
    } finally {
      if (alive()) setLoadingSummary(false);
    }
  }, []);

  const loadChart = useCallback(async (r: TimeRangeValue, alive: Alive) => {
    setLoadingChart(true); setChartError(null);
    try {
      const data = await googleAdsLandingApi.getTimeseries(r);
      if (!alive()) return;
      setSeries(data);
    } catch (e) {
      if (!alive()) return;
      setChartError(e instanceof Error ? e.message : 'Unknown error');
    } finally {
      if (alive()) setLoadingChart(false);
    }
  }, []);

  const loadTables = useCallback(async (r: TimeRangeValue, alive: Alive) => {
    setLoadingTables(true); setTableError(null);
    try {
      const [c, u] = await Promise.all([
        googleAdsLandingApi.getCtaBreakdown(r),
        googleAdsLandingApi.getUtmBreakdown(r),
      ]);
      if (!alive()) return;
      setCta(c); setUtm(u);
    } catch (e) {
      if (!alive()) return;
      setTableError(e instanceof Error ? e.message : 'Unknown error');
    } finally {
      if (alive()) setLoadingTables(false);
    }
  }, []);

  const loadCampaigns = useCallback(async (_r: undefined, alive: Alive) => {
    setLoadingCampaigns(true); setCampaignsError(null);
    try {
      const data = await googleAdsLandingApi.getCampaigns();
      if (!alive()) return;
      setCampaigns(data);
    } catch (e) {
      if (!alive()) return;
      setCampaignsError(e instanceof Error ? e.message : 'Unknown error');
    } finally {
      if (alive()) setLoadingCampaigns(false);
    }
  }, []);

  // One sequence counter per resource. A run takes the next number and is "alive"
  // only while it is still the latest, so the effect, a range change and the
  // Refresh / Retry buttons all go through the same guard: the newest request wins,
  // and a stale response can never repaint over it. (The buttons used to call the
  // loaders without the liveness probe, which they require, so a refresh never finished.)
  const summarySeq = useRef(0);
  const chartSeq = useRef(0);
  const tableSeq = useRef(0);
  const campaignsSeq = useRef(0);
  const run = <R,>(seq: React.MutableRefObject<number>, loader: (r: R, alive: Alive) => Promise<void>, r: R) => {
    seq.current += 1;
    const id = seq.current;
    return loader(r, () => seq.current === id);
  };

  useEffect(() => {
    run(summarySeq, loadSummary, summaryRange);
    return () => { summarySeq.current += 1; };
  }, [summaryRange, loadSummary]);

  useEffect(() => {
    run(chartSeq, loadChart, chartRange);
    return () => { chartSeq.current += 1; };
  }, [chartRange, loadChart]);

  useEffect(() => {
    run(tableSeq, loadTables, tableRange);
    return () => { tableSeq.current += 1; };
  }, [tableRange, loadTables]);

  useEffect(() => {
    run(campaignsSeq, loadCampaigns, undefined);
    return () => { campaignsSeq.current += 1; };
  }, [loadCampaigns]);

  const refreshSummary = () => run(summarySeq, loadSummary, summaryRange);
  const refreshChart = () => run(chartSeq, loadChart, chartRange);
  const refreshTables = () => run(tableSeq, loadTables, tableRange);
  const refreshCampaigns = () => run(campaignsSeq, loadCampaigns, undefined);

  const ads = summary?.ads;
  const ratios = summary?.ratios;
  const landings = num(summary?.landings.total);
  const paidLandings = num(summary?.landings.paid);
  const storeClicks = num(summary?.store_clicks.total);

  return (
    <Box sx={{ p: 3 }}>
      {/* Header */}
      <Box sx={{ mb: 3 }}>
        <Typography component="h1" sx={{ fontFamily: brandFonts.heading, fontSize: '26px', fontWeight: 600, color: '#111827' }}>
          Google Ads
        </Typography>
        <Typography sx={{ fontFamily: brandFonts.accent, fontSize: '12px', color: brandColors.slateGray }}>
          Campaign performance from the Google Ads API, then what the paid landing page does with the clicks
        </Typography>
      </Box>

      {/* 1. Performance — the Ad Dashboard's Google band, framed */}
      <SectionHeading
        title="Performance"
        sub="The same Google band as the Ad Dashboard: same code, same readings, updated nightly"
        action={(
          <Button size="small" component={RouterLink} to="/ads"
            sx={{ fontFamily: brandFonts.accent, fontSize: '11px', textTransform: 'none', color: brandColors.deepTeal }}>
            Open the Ad Dashboard
          </Button>
        )}
      />
      <Box sx={{ mb: 4 }}>
        <PerformanceFrame />
      </Box>

      {/* 2. Landing page */}
      <SectionHeading
        title="Landing page /first-chapter"
        sub={(
          <>
            Paid landing page ·{' '}
            <a href={LANDING_URL} target="_blank" rel="noopener noreferrer" style={{ color: brandColors.deepTeal }}>
              /first-chapter
            </a>
          </>
        )}
        action={<TimeRangeFilter value={summaryRange} onChange={setSummaryRange} onRefresh={refreshSummary} refreshing={loadingSummary} />}
      />

      <RangeMetaLine meta={summary?.range_meta} />

      {summaryError ? (
        <EmptyState icon="error" title="Could not load the summary" description={summaryError}
          action={{ label: 'Retry', onClick: refreshSummary, variant: 'contained' }} />
      ) : (
        <>
          {/* Funnel */}
          <Funnel
            loading={loadingSummary}
            steps={[
              {
                label: 'Ad clicks',
                value: ads?.connected ? fmtInt(ads.clicks) : 'Not connected',
                color: brandColors.deepTeal,
                note: ads?.connected ? `${fmtInt(ads.impressions)} impressions` : undefined,
                unavailable: !ads?.connected,
              },
              {
                label: 'Landings',
                value: fmtInt(landings),
                color: brandColors.forestGreen,
                note: ratios?.click_delivery_rate != null
                  ? `${fmtPct(ratios.click_delivery_rate)} of ad clicks arrived as paid landings`
                  : undefined,
              },
              {
                label: 'Store clicks',
                value: fmtInt(storeClicks),
                color: brandColors.warmGold,
                note: `${fmtPct(ratios?.landing_to_store_ctr)} of landings`,
              },
              {
                label: 'Attributed signups',
                value: 'Not measured',
                color: brandColors.richBurgundy,
                note: 'no click→install join exists',
                unavailable: true,
              },
            ]}
          />
          <Typography sx={{ fontFamily: brandFonts.accent, fontSize: '11px', color: brandColors.slateGray, mb: 3 }}>
            <strong>Landings counts page views, not visitors, and the gap is large.</strong> Every in-page anchor
            click writes another page view — not just the two CTAs, but the nav links (How It Works, Reviews, Stories,
            FAQs) and the logo. One visitor who taps a CTA is 2 landings; one who browses the nav can be 5 or 6.
            Nothing stored distinguishes those extra rows from a genuine landing, so <strong>Landings can be several
            times the real visitor count, and every rate below is understated by the same factor.</strong> Treat the
            direction of travel as meaningful and the absolute rates as a floor.
          </Typography>

          {/* KPIs */}
          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)', lg: 'repeat(4, 1fr)' }, gap: 2, mb: 3 }}>
            <Kpi label="Landings" topColor={brandColors.forestGreen} loading={loadingSummary}
              value={fmtInt(landings)}
              sub={`${fmtInt(paidLandings)} paid · ${fmtInt(summary?.landings.organic_and_other)} organic/other — ${fmtInt(summary?.landings.mobile)} mobile · ${fmtInt(summary?.landings.tablet)} tablet · ${fmtInt(summary?.landings.desktop)} desktop`}
              help="Page views of /first-chapter. Includes the extra view an in-page anchor CTA generates — see the note under the funnel." />
            <Kpi label="Landing → store CTR" topColor={brandColors.warmGold} loading={loadingSummary}
              value={fmtPct(ratios?.landing_to_store_ctr)}
              sub={`${fmtInt(storeClicks)} store clicks`}
              help="Store clicks ÷ all landings (paid and organic)." />
            <Kpi label="Cost per store click" topColor={brandColors.deepTeal} loading={loadingSummary}
              muted={!ads?.connected}
              value={ads?.connected ? fmtUsd(ratios?.cost_per_store_click) : 'Not connected'}
              sub={ads?.connected ? `${fmtUsd(ads.cost_usd)} spent` : 'No Google Ads reading stored yet'}
              help="Spend ÷ store clicks." />
            <Kpi label="Engagement" topColor={brandColors.richBurgundy} loading={loadingSummary}
              value={fmtTime(summary?.landings.avg_time_sec ?? null)}
              sub={summary?.landings.avg_scroll_pct != null ? `${summary.landings.avg_scroll_pct}% avg scroll · read the caveat` : 'no engagement data yet'}
              help="Understates real engagement, for three compounding reasons: the extra page view an in-page anchor click creates is a near-zero-duration row that drags the average down; measurement stops at the first time the visitor backgrounds the tab and never resumes; and views that never reported at all are excluded from the average rather than counted as zero. Directionally useful, not an accurate time-on-page." />
          </Box>

          {/* Google's side of the funnel, and the store split */}
          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)', lg: 'repeat(3, 1fr)' }, gap: 2, mb: ads?.connected ? 1 : 3 }}>
            <Kpi label="Ad clicks" topColor={brandColors.deepTeal} loading={loadingSummary}
              muted={!ads?.connected}
              value={ads?.connected ? fmtInt(ads.clicks) : 'Not connected'}
              sub={ads?.connected ? `${fmtInt(ads.impressions)} impressions · ${fmtUsd(ads.cost_usd)} spent` : 'No Google Ads reading stored yet'}
              help="Clicks Google reports for the window, from the Google Ads API readings, pro-rated by day." />
            <Kpi label="Click delivery rate" topColor={brandColors.forestGreen} loading={loadingSummary}
              muted={!ads?.connected}
              value={ads?.connected ? fmtPct(ratios?.click_delivery_rate) : 'Not connected'}
              sub={ads?.connected ? `${fmtInt(paidLandings)} paid landings ÷ ${fmtInt(ads.clicks)} ad clicks` : undefined}
              help="Paid landings ÷ Google's ad clicks. Well under 100% means visitors are lost before the page tracker runs (slow load, blockers), so every number downstream is understated. It does not mean the ad is underperforming." />
            <Kpi label="Store mix" topColor={brandColors.warmGold} loading={loadingSummary}
              value={storeClicks > 0 ? `${fmtPct(ratios?.ios_share)} iOS` : dash}
              sub={storeClicks > 0
                ? `${fmtInt(summary?.store_clicks.ios)} App Store · ${fmtInt(summary?.store_clicks.android)} Google Play`
                : 'no store clicks in this window'} />
          </Box>
          {!loadingSummary && ads?.connected && ads.note && (
            <Typography sx={{ fontFamily: brandFonts.accent, fontSize: '10.5px', color: brandColors.slateGray, mb: 3 }}>
              Cost and clicks: {ads.note}
            </Typography>
          )}
          {!loadingSummary && ads && !ads.connected && (
            <Paper sx={{ p: 2.5, borderRadius: '14px', border: '1px solid #E5E7EB', borderTop: `3px solid ${brandColors.slateGray}`, boxShadow: 'none', mb: 3 }}>
              <Typography sx={{ fontFamily: brandFonts.accent, fontSize: '10px', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.7px', color: brandColors.slateGray, mb: 0.5 }}>
                Cost data not connected
              </Typography>
              <Typography sx={{ fontFamily: brandFonts.accent, fontSize: '12px', color: '#374151', lineHeight: 1.5 }}>
                {ads.not_connected_reason}
              </Typography>
            </Paper>
          )}
        </>
      )}

      {/* Attribution — an explanation, not a chart */}
      {summary?.attributed_signups && (
        <Paper sx={{ p: 2.5, borderRadius: '14px', border: '1px solid #E5E7EB', borderTop: `3px solid ${brandColors.richBurgundy}`, boxShadow: 'none', mb: 3 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.8 }}>
            <Typography sx={{ fontFamily: brandFonts.accent, fontSize: '10px', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.7px', color: brandColors.slateGray }}>
              Attributed signups
            </Typography>
            <Chip label="NOT MEASURED" size="small"
              sx={{ height: 18, fontFamily: brandFonts.accent, fontSize: '9px', fontWeight: 800, bgcolor: brandColors.softCream, color: brandColors.richBurgundy }} />
          </Box>
          <Typography sx={{ fontFamily: brandFonts.accent, fontSize: '12.5px', color: '#374151', lineHeight: 1.55, mb: 0.8 }}>
            {summary.attributed_signups.reason}
          </Typography>
          <Typography sx={{ fontFamily: brandFonts.accent, fontSize: '12px', color: brandColors.deepTeal, lineHeight: 1.55 }}>
            <strong>To switch this on:</strong> {summary.attributed_signups.unlock}
          </Typography>
        </Paper>
      )}

      {/* Trend */}
      <Paper sx={{ p: 2.5, borderRadius: '14px', border: '1px solid #E5E7EB', boxShadow: 'none', mb: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2, flexWrap: 'wrap' }}>
          <Typography sx={{ fontFamily: brandFonts.accent, fontSize: '12px', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.7px', color: brandColors.slateGray }}>
            Landings, store clicks and spend
          </Typography>
          <Box sx={{ flexGrow: 1 }} />
          <TimeRangeFilter value={chartRange} onChange={setChartRange} onRefresh={refreshChart} refreshing={loadingChart} size="small" />
        </Box>
        {chartError ? (
          <EmptyState icon="error" title="Could not load the trend" description={chartError}
            action={{ label: 'Retry', onClick: refreshChart, variant: 'contained' }} />
        ) : loadingChart ? (
          <Skeleton variant="rectangular" height={300} />
        ) : !series?.series?.length ? (
          <EmptyState icon="info" title="No traffic in this window"
            description="Nothing has landed on /first-chapter in the selected range. This is an empty window, not a failed load." />
        ) : (
          <ResponsiveContainer width="100%" height={320}>
            <ComposedChart data={series.series}>
              <CartesianGrid strokeDasharray="3 3" stroke="#E5E7EB" />
              <XAxis dataKey="bucket" tick={{ fontSize: 11, fontFamily: brandFonts.accent }} />
              <YAxis yAxisId="left" tick={{ fontSize: 11, fontFamily: brandFonts.accent }} />
              <YAxis yAxisId="right" orientation="right" tick={{ fontSize: 11, fontFamily: brandFonts.accent }}
                tickFormatter={(v) => `$${v}`} />
              <ReTooltip contentStyle={{ fontFamily: brandFonts.accent, fontSize: 12, borderRadius: 10 }} />
              <Legend wrapperStyle={{ fontFamily: brandFonts.accent, fontSize: 11 }} />
              <Bar yAxisId="left" dataKey="landings" name="Landings" fill={brandColors.forestGreen} radius={[3, 3, 0, 0]} />
              <Bar yAxisId="left" dataKey="store_clicks" name="Store clicks" fill={brandColors.warmGold} radius={[3, 3, 0, 0]} />
              <Line yAxisId="right" type="monotone" dataKey="cost_usd" name="Spend (USD)" stroke={brandColors.deepTeal} strokeWidth={2} dot={false} />
            </ComposedChart>
          </ResponsiveContainer>
        )}
        {!chartError && !loadingChart && series?.cost_source && (
          <Typography sx={{ fontFamily: brandFonts.accent, fontSize: '10.5px', color: brandColors.slateGray, mt: 1 }}>
            Spend: {series.cost_source}. Weekly totals are exact; the daily shape inside a week is flat.
          </Typography>
        )}
      </Paper>

      {/* Breakdowns */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 1.5, flexWrap: 'wrap' }}>
        <Typography sx={{ fontFamily: brandFonts.accent, fontSize: '12px', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.7px', color: brandColors.slateGray }}>
          Breakdowns
        </Typography>
        <Box sx={{ flexGrow: 1 }} />
        <Button size="small" startIcon={<DownloadIcon />} href={googleAdsLandingApi.exportCsvUrl(tableRange)}
          sx={{ fontFamily: brandFonts.accent, fontSize: '11px', textTransform: 'none', color: brandColors.deepTeal }}>
          Export store clicks
        </Button>
        <TimeRangeFilter value={tableRange} onChange={setTableRange} onRefresh={refreshTables} refreshing={loadingTables} size="small" />
      </Box>

      {tableError ? (
        <EmptyState icon="error" title="Could not load the breakdowns" description={tableError}
          action={{ label: 'Retry', onClick: refreshTables, variant: 'contained' }} />
      ) : (
        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', lg: '1fr 1fr' }, gap: 2 }}>
          {/* Which CTA */}
          <Paper sx={{ p: 2.5, borderRadius: '14px', border: '1px solid #E5E7EB', boxShadow: 'none' }}>
            <Typography sx={{ fontFamily: brandFonts.accent, fontSize: '11px', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.7px', color: brandColors.slateGray, mb: 1.5 }}>
              Which CTA gets clicked
            </Typography>
            {loadingTables ? <Skeleton variant="rectangular" height={180} />
              : !cta?.rows?.length ? <EmptyState icon="info" title="No CTA clicks yet" description="Nothing has been clicked on this page in the selected window." />
              : (
                <Box component="table" sx={{ width: '100%', borderCollapse: 'collapse', fontFamily: brandFonts.accent, fontSize: '12px' }}>
                  <Box component="thead">
                    <Box component="tr" sx={{ '& th': { textAlign: 'left', pb: 1, color: brandColors.slateGray, fontSize: '10px', textTransform: 'uppercase', letterSpacing: '0.6px', borderBottom: '1px solid #E5E7EB' } }}>
                      <th>CTA</th><th style={{ textAlign: 'right' }}>Clicks</th><th style={{ textAlign: 'right' }}>Share</th>
                    </Box>
                  </Box>
                  <Box component="tbody">
                    {cta.rows.map((r) => (
                      <Box component="tr" key={r.cta_id} sx={{ '& td': { py: 0.9, borderBottom: '1px solid #F3F4F6' } }}>
                        <td>{r.cta_label || r.cta_id}</td>
                        <td style={{ textAlign: 'right', fontWeight: 700 }}>{fmtInt(r.clicks)}</td>
                        <td style={{ textAlign: 'right', color: brandColors.slateGray }}>{r.pct_of_total === null ? dash : `${r.pct_of_total}%`}</td>
                      </Box>
                    ))}
                  </Box>
                </Box>
              )}
            {cta?.note && !loadingTables && (
              <Typography sx={{ fontFamily: brandFonts.accent, fontSize: '10.5px', color: brandColors.slateGray, mt: 1.5 }}>
                {cta.note}
              </Typography>
            )}
          </Paper>

          {/* Which campaign / keyword */}
          <Paper sx={{ p: 2.5, borderRadius: '14px', border: '1px solid #E5E7EB', boxShadow: 'none' }}>
            <Typography sx={{ fontFamily: brandFonts.accent, fontSize: '11px', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.7px', color: brandColors.slateGray, mb: 1.5 }}>
              Store clicks by campaign &amp; keyword
            </Typography>
            {loadingTables ? <Skeleton variant="rectangular" height={180} />
              : !utm?.rows?.length ? (
                <EmptyState icon="info" title="No tagged store clicks yet"
                  description="Store clicks appear here once ad traffic arrives with UTM parameters on the URL." />
              ) : (
                <Box sx={{ overflowX: 'auto' }}>
                  <Box component="table" sx={{ width: '100%', minWidth: 460, borderCollapse: 'collapse', fontFamily: brandFonts.accent, fontSize: '12px' }}>
                    <Box component="thead">
                      <Box component="tr" sx={{ '& th': { textAlign: 'left', pb: 1, color: brandColors.slateGray, fontSize: '10px', textTransform: 'uppercase', letterSpacing: '0.6px', borderBottom: '1px solid #E5E7EB', whiteSpace: 'nowrap' } }}>
                        <th>Campaign</th><th>Keyword</th><th style={{ textAlign: 'right' }}>iOS</th><th style={{ textAlign: 'right' }}>Android</th><th style={{ textAlign: 'right' }}>Total</th>
                      </Box>
                    </Box>
                    <Box component="tbody">
                      {utm.rows.map((r, i) => (
                        <Box component="tr" key={`${r.utm_campaign}-${r.utm_term}-${i}`} sx={{ '& td': { py: 0.9, borderBottom: '1px solid #F3F4F6' } }}>
                          <td>{r.utm_campaign}</td>
                          <td style={{ color: brandColors.slateGray }}>{r.utm_term}</td>
                          <td style={{ textAlign: 'right' }}>{fmtInt(r.ios)}</td>
                          <td style={{ textAlign: 'right' }}>{fmtInt(r.android)}</td>
                          <td style={{ textAlign: 'right', fontWeight: 700 }}>{fmtInt(r.total)}</td>
                        </Box>
                      ))}
                    </Box>
                  </Box>
                </Box>
              )}
          </Paper>
        </Box>
      )}

      {/* 3. Campaigns */}
      <Box sx={{ mt: 4 }}>
        <SectionHeading
          title="Campaigns"
          sub={campaigns?.latest_week
            ? `Latest week ${campaigns.latest_week.label || `to ${fmtDay(campaigns.latest_week.friday)}`}${campaigns.latest_week.partial ? ' · partial week, still accruing' : ''}`
            : 'Newest stored week per campaign, with a four-week spend trend'}
          action={(
            <Button size="small" component={RouterLink} to="/ad-report"
              sx={{ fontFamily: brandFonts.accent, fontSize: '11px', textTransform: 'none', color: brandColors.deepTeal }}>
              Agency commentary → SVD Ad Report
            </Button>
          )}
        />
        <Paper sx={{ p: 2.5, borderRadius: '14px', border: '1px solid #E5E7EB', boxShadow: 'none' }}>
          {campaignsError ? (
            <EmptyState icon="error" title="Could not load the campaigns" description={campaignsError}
              action={{ label: 'Retry', onClick: refreshCampaigns, variant: 'contained' }} />
          ) : loadingCampaigns ? (
            <Skeleton variant="rectangular" height={140} />
          ) : !campaigns?.campaigns?.length ? (
            <EmptyState icon="info" title="No Google campaign readings yet"
              description="The nightly Google Ads sync has not stored a week. This is an empty table, not a failed load." />
          ) : (
            <>
              <CampaignsTable data={campaigns} />
              <Typography sx={{ fontFamily: brandFonts.accent, fontSize: '10.5px', color: brandColors.slateGray, mt: 1.5 }}>
                Weeks run Saturday to Friday. Conversions are all Google conversion actions, not only first chapters.
                Cost per conversion is blank, not $0, when a campaign had none. A week in progress is not compared with a full one.
              </Typography>
            </>
          )}
        </Paper>
      </Box>

      <Typography sx={{ fontFamily: brandFonts.accent, fontSize: '11px', color: brandColors.slateGray, mt: 3 }}>
        Coming later: keywords, search terms, asset groups and the conversion-action split. Each needs a new Google Ads query.
      </Typography>
    </Box>
  );
};

export default GoogleAdsLandingPage;
export { GoogleAdsLandingPage };
